import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useVendors } from '../context/VendorsContext';
import { uploadImageToSupabase, deleteImageFromSupabase } from '../utils/imageUpload';
import Layout from '../components/Layout';
import './VendorForm.css';

const VendorForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { vendors, createVendor, updateVendor } = useVendors();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    image_url: null,
    imagePreview: null,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (isEditMode) {
      const vendor = vendors.find((v) => v.id === id);
      if (vendor) {
        setFormData({
          name: vendor.name || '',
          description: vendor.description || '',
          url: vendor.url || '',
          image_url: vendor.image_url || null,
          imagePreview: vendor.image_url || null,
        });
      }
    }
  }, [id, isEditMode, vendors]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.name.trim()) {
      setError('Vendr name is required');
      setLoading(false);
      return;
    }

    try {
      let finalImageUrl = formData.image_url;

      // Upload new image if a file was selected
      if (imageFile) {
        setUploadingImage(true);
        
        // If editing and there's an old image, delete it first
        if (isEditMode && formData.image_url && !formData.image_url.startsWith('data:')) {
          await deleteImageFromSupabase(formData.image_url);
        }

        const uploadResult = await uploadImageToSupabase(imageFile, isEditMode ? id : null);
        setUploadingImage(false);

        if (!uploadResult.success) {
          setError(uploadResult.error || 'Failed to upload image');
          setLoading(false);
          return;
        }

        finalImageUrl = uploadResult.url;
      }

      // Prepare vendr data
      const vendorData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        url: formData.url.trim() || null,
        image_url: finalImageUrl || null,
      };

      const result = isEditMode
        ? await updateVendor(id, vendorData)
        : await createVendor(vendorData);

      if (result.success) {
        navigate('/vendors');
      } else {
        setError(result.error || 'An error occurred. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      setError(error.message || 'An error occurred. Please try again.');
      setLoading(false);
      setUploadingImage(false);
    }
  };

  return (
    <Layout>
      <div className="vendor-form">
        <div className="form-header">
          <h1>{isEditMode ? 'Edit Vendr' : 'Create New Vendr'}</h1>
          <button onClick={() => navigate('/vendors')} className="back-button">
            ← Back to Vendrs
          </button>
        </div>

        <form onSubmit={handleSubmit} className="vendor-form-content">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="name">Vendr Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter vendr name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Enter vendr description (optional)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="url">Website URL</label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleChange}
              placeholder="https://example.com (optional)"
            />
            <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
              This URL will be used to link the vendr card on the main website
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="image">Vendr Image</label>
            <input
              type="file"
              id="image"
              name="image"
              accept="image/*"
              onChange={handleImageChange}
            />
            {formData.imagePreview && (
              <div className="image-preview">
                <img src={formData.imagePreview} alt="Preview" />
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/vendors')}
              className="cancel-button"
            >
              Cancel
            </button>
            <button type="submit" className="submit-button" disabled={loading || uploadingImage}>
              {uploadingImage ? 'Uploading image...' : loading ? 'Saving...' : isEditMode ? 'Update Vendr' : 'Create Vendr'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default VendorForm;

